import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { readCmsCache, writeCmsCache } from "../lib/cms-cache";
import { ensureServiceableFix } from "../lib/serviceability";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { loadCart, saveCart, setQty, bumpQty, cartCount, priceCart, hydrateGuestCart } from "../lib/cart";
import { useAuth } from "../context/AuthContext";
import {
  ShoppingBag, Search, ArrowRight, Truck, RefreshCw, X,
} from "lucide-react";
import HeroPanel from "../components/restaurant/HeroPanel";
import CategoryStrip from "../components/restaurant/CategoryStrip";
import DishCard from "../components/restaurant/DishCard";
import DishDetailModal from "../components/restaurant/DishDetailModal";
import PromotionPopup from "../components/PromotionPopup";
import ServiceabilityPill from "../components/ServiceabilityPill";
import TodayMessMenuFlash from "../components/TodayMessMenuFlash";
import TrustChipsMarquee from "../components/TrustChipsMarquee";
import RestaurantClosedBanner from "../components/RestaurantClosedBanner";
import SEO from "../components/SEO";

// 8 trust chips — what we promise (and don't) about the food. Brand-defining
// commitment, intentionally kept in code (not CMS-editable).
const TRUST_CHIPS_DEPRECATED = []; // moved to components/TrustChipsMarquee.jsx

/**
 * Restaurant browse — Zomato-style:
 *   • HeroPanel (3D + parallax tilt) on top
 *   • CategoryStrip (floating Lucide icons + label) below search
 *   • DishCard grid with 3D depth + glassmorphism category badges
 *   • Sticky cart pill at the bottom
 */
export default function Restaurant() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [menu, setMenu] = useState(null);
  const [meta, setMeta] = useState({ delivery_fee_flat: 50, delivery_free_over: 500 });
  const [cart, setCart] = useState(loadCart());
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const [lastOrder, setLastOrder] = useState(null);
  const [reorderDismissed, setReorderDismissed] = useState(false);
  const [theme, setTheme] = useState(() => readCmsCache("/restaurant/theme"));
  const [activeOrder, setActiveOrder] = useState(null);
  // Admin-curated category list (CMS) — falls back to deriving from menu items
  // if the categories doc is empty / hasn't been bootstrapped yet.
  const [cmsCategories, setCmsCategories] = useState(null);
  // Tap-image-to-expand detail modal — opens for the clicked menu item
  const [openItem, setOpenItem] = useState(null);

  // Mount-once: load menu, theme, categories, hydrate guest cart from server.
  // Intentionally an empty-deps effect — `api` / `hydrateGuestCart` are module-
  // scope singletons; running this every render would refetch on every state
  // change which we explicitly don't want.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/restaurant/menu")
      .then((r) => { setMenu(r.data.items || []); setMeta({ delivery_fee_flat: r.data.delivery_fee_flat, delivery_free_over: r.data.delivery_free_over }); })
      .catch(() => toast.error("Could not load menu"));
    api.get("/restaurant/theme").then((r) => { setTheme(r.data || null); writeCmsCache("/restaurant/theme", r.data); }).catch((err) => console.warn("[restaurant] theme load failed", err));
    api.get("/restaurant/categories").then((r) => setCmsCategories(r.data?.categories || null)).catch((err) => console.warn("[restaurant] categories load failed", err));
    hydrateGuestCart().then((merged) => setCart(merged)).catch((err) => console.warn("[restaurant] guest-cart hydrate failed", err));
  }, []);

  // Pull most recent delivered order for the "reorder in 1 tap" banner
  // AND any in-flight (out_for_delivery / preparing / etc) for the live tracking pill.
  useEffect(() => {
    if (!user) { setLastOrder(null); setActiveOrder(null); return; }
    try {
      if (sessionStorage.getItem("efc_reorder_dismissed_v1") === "1") setReorderDismissed(true);
    } catch (err) {
      // Private/incognito browsers can throw on sessionStorage access — non-critical.
      console.debug("[restaurant] sessionStorage unavailable", err);
    }
    api.get("/restaurant/orders?limit=10")
      .then((r) => {
        const rows = r.data?.orders || [];
        const delivered = rows.find((o) => o.status === "delivered");
        if (delivered) setLastOrder(delivered);
        const live = rows.find((o) => ["paid", "preparing", "ready_for_pickup", "out_for_delivery"].includes(o.status));
        if (live) setActiveOrder(live);
      })
      .catch((err) => console.warn("[restaurant] orders fetch failed", err));
  }, [user]);

  useEffect(() => { saveCart(cart); }, [cart]);

  const categories = useMemo(() => {
    // Prefer the admin-curated list when available; intersect with what's
    // actually present in the menu to avoid empty category pills.
    const presentInMenu = new Set((menu || []).map((m) => m.category || "Mains"));
    if (cmsCategories && cmsCategories.length) {
      const filtered = cmsCategories.filter((c) => presentInMenu.has(c));
      return ["All", ...(filtered.length ? filtered : cmsCategories)];
    }
    return ["All", ...Array.from(presentInMenu)];
  }, [menu, cmsCategories]);

  const filtered = useMemo(() => {
    if (!menu) return [];
    const ql = q.trim().toLowerCase();
    return menu.filter((m) => {
      if (activeCat !== "All" && m.category !== activeCat) return false;
      if (!ql) return true;
      return (m.name || "").toLowerCase().includes(ql) || (m.description || "").toLowerCase().includes(ql);
    });
  }, [menu, q, activeCat]);

  const cartLines = priceCart(cart, menu || []);
  const totalCount = cartCount(cart);

  const onAdd = (it, variant = "regular") => setCart((c) => bumpQty(c, it.id, 1, variant));
  const onSub = (it, variant = "regular") => setCart((c) => bumpQty(c, it.id, -1, variant));

  // iter-61 #5: gate buy-now / checkout on a serviceable location fix. If
  // the user already has a cached in-range fix this resolves instantly; if
  // not, the helper triggers a fresh GPS read and persists lat/lng so the
  // backend's _enforce_serviceable_area check passes.
  const requireServiceable = async () => {
    const fix = await ensureServiceableFix({ persistToUser: !!user });
    if (fix.ok) return true;
    if (fix.reason === "out-of-range") {
      toast.error(`You're ${fix.km} km away — outside our ${fix.radius} km zone. We can't deliver here.`);
    } else if (fix.reason === "permission-denied") {
      toast.error("Enable location access to continue. Open browser site settings → unblock Location.");
    } else {
      toast.error("Couldn't read your location. Try again with GPS on.");
    }
    return false;
  };

  // Buy now: stash one-item cart in sessionStorage and jump to checkout
  // (login wall handles redirect). The dish detail modal passes the chosen
  // portion variant so checkout shows "Butter Chicken · Large" not just qty.
  const buyNow = async (it, variant = "regular") => {
    if (!(await requireServiceable())) return;
    const v = (variant || "regular").toLowerCase();
    try {
      sessionStorage.setItem(
        "efc_buynow_v1",
        JSON.stringify({ [`${it.id}::${v}`]: { id: it.id, variant: v, qty: 1 } })
      );
    } catch (e) { console.warn("[Restaurant] buynow sessionStorage write failed", e); }
    if (!user) {
      try { sessionStorage.setItem("efc_pending_action_v1", "/restaurant/checkout?buynow=1"); }
      catch (e) { console.warn("[Restaurant] pending-action sessionStorage write failed", e); }
      navigate(`/login?next=${encodeURIComponent("/restaurant/checkout?buynow=1")}`);
      return;
    }
    navigate("/restaurant/checkout?buynow=1");
  };

  const goCheckout = async () => {
    if (totalCount === 0) { toast.error("Your cart is empty"); return; }
    if (!(await requireServiceable())) return;
    if (!user) {
      try { sessionStorage.setItem("efc_pending_action_v1", "/restaurant/checkout"); }
      catch (e) { console.warn("[Restaurant] pending-action sessionStorage write failed", e); }
      navigate(`/login?next=${encodeURIComponent("/restaurant/checkout")}`);
      return;
    }
    navigate("/restaurant/checkout");
  };

  const reorderNow = () => {
    if (!lastOrder || !menu) return;
    const liveIds = new Set(menu.map((m) => m.id));
    let next = { ...cart };
    let added = 0;
    let skipped = 0;
    for (const line of lastOrder.items || []) {
      if (!liveIds.has(line.id)) { skipped += 1; continue; }
      const variant = (line.variant || "regular").toLowerCase();
      const key = `${line.id}::${variant}`;
      const cur = next[key]?.qty || 0;
      next = setQty(next, line.id, cur + (line.qty || 1), variant);
      added += 1;
    }
    setCart(next);
    if (added === 0) { toast.error("None of those items are available right now"); return; }
    if (skipped > 0) toast.warning(`${skipped} item(s) no longer available — skipped`);
    toast.success(`Reordered ${added} item${added > 1 ? "s" : ""}`);
    navigate("/restaurant/checkout");
  };

  const dismissReorder = () => {
    setReorderDismissed(true);
    try { sessionStorage.setItem("efc_reorder_dismissed_v1", "1"); }
    catch (e) { console.warn("[Restaurant] reorder-dismissed sessionStorage write failed", e); }
  };

  return (
    <div className="min-h-screen bg-background pb-40 md:pb-32" data-testid="restaurant-page">
      <SEO
        title="Order online · 90-min fresh meal delivery"
        path="/restaurant"
        description="Order zero-adulteration home-style meals from efoodcare — 90-minute fresh delivery. No Ajinomoto, No Maida, No Refined or Palm oil. Subscription meal plans and à-la-carte tiffins."
      />
      <HeroPanel theme={theme} meta={meta} />
      {/* iter-88 #3: ~8 px breathing room between hero container and the
          kitchen / location status strip. */}
      <div className="h-2 sm:h-2.5" />
      <RestaurantClosedBanner />
      <ServiceabilityPill />

      <div className="max-w-6xl mx-auto px-3 sm:px-5">
        {/* Mess menu daily flash — top of the menu container per iter-62 #8 */}
        <div className="mt-3">
          <TodayMessMenuFlash compact />
        </div>
        {/* Trust chips — reusable marquee */}
        <TrustChipsMarquee className="mt-3 -mb-1" />

        {/* Live tracking pill — when a restaurant order is in-flight */}
        {user && activeOrder && (
          <Link
            to={`/restaurant/track/${activeOrder.order_id}`}
            className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900/40 p-3.5 hover:bg-emerald-100/70 dark:hover:bg-emerald-900/40 transition-colors group"
            data-testid="active-track-pill"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="relative h-10 w-10 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60"></span>
                <Truck className="h-5 w-5 relative z-10" />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] tracking-overline uppercase font-bold text-emerald-700 dark:text-emerald-300">Order in progress</p>
                <p className="font-display font-extrabold text-sm leading-tight mt-0.5 truncate">
                  {activeOrder.status === "out_for_delivery" ? "Rider on the way · Track live" : "Tap to track your order"}
                </p>
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-emerald-700 dark:text-emerald-300 group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </Link>
        )}

        {/* Reorder banner — most recent delivered order, 1-tap CTA */}
        {user && lastOrder && !reorderDismissed && (
          <div
            className="mt-4 rounded-2xl border border-primary/30 bg-primary/5 p-4 sm:p-5 flex items-center gap-3 sm:gap-4"
            data-testid="reorder-banner"
          >
            <div className="hidden sm:flex h-12 w-12 rounded-full bg-primary/15 items-center justify-center flex-shrink-0">
              <RefreshCw className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-overline uppercase font-bold text-primary">Welcome back</p>
              <p className="font-display font-extrabold text-base sm:text-lg leading-tight mt-0.5 truncate">
                Reorder your last meal · ₹{Number(lastOrder.total).toFixed(0)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {(lastOrder.items || []).map((i) => `${i.name} × ${i.qty}`).join(" · ")}
              </p>
            </div>
            <Button
              onClick={reorderNow}
              size="sm"
              className="rounded-full bg-primary hover:bg-primary/90 flex-shrink-0 h-9 px-4 text-xs sm:text-sm"
              data-testid="reorder-banner-cta"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Reorder
            </Button>
            <button
              type="button"
              onClick={dismissReorder}
              className="text-muted-foreground hover:text-foreground flex-shrink-0"
              aria-label="Dismiss"
              data-testid="reorder-banner-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Search */}
        <div className="sticky top-0 z-20 -mx-3 sm:-mx-5 px-3 sm:px-5 pt-3.5 pb-2.5 bg-background/95 backdrop-blur border-b border-border">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={theme?.search_placeholder || "Search dishes…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-9 rounded-full text-sm"
              data-testid="restaurant-search"
            />
          </div>
        </div>

        <CategoryStrip categories={categories} activeCat={activeCat} onChange={setActiveCat} />

        {/* Items grid */}
        <div className="mt-3">
          {!menu ? (
            <p className="text-center text-muted-foreground py-12">Loading menu…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-12" data-testid="restaurant-no-items">No items match your search.</p>
          ) : (
            <ul
              className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 pt-2 pb-4"
              data-testid="restaurant-items"
              style={{ perspective: "1200px" }}
            >
              {filtered.map((it, idx) => (
                <DishCard
                  key={it.id}
                  it={it}
                  qty={cart[`${it.id}::regular`]?.qty || 0}
                  theme={theme}
                  idx={idx}
                  onAdd={onAdd}
                  onSub={onSub}
                  onBuy={buyNow}
                  onOpenDetail={setOpenItem}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Sticky cart bar — sits ABOVE BottomNav */}
      {totalCount > 0 && (
        <div className="fixed bottom-20 md:bottom-6 inset-x-0 z-40 px-4" data-testid="cart-bar">
          <div className="max-w-2xl mx-auto rounded-full bg-foreground text-background shadow-2xl flex items-center justify-between gap-3 px-5 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <ShoppingBag className="h-5 w-5 flex-shrink-0" />
              <div className="text-sm min-w-0">
                <p className="font-bold leading-tight truncate" data-testid="cart-count">
                  {totalCount} item{totalCount > 1 ? "s" : ""} · ₹{cartLines.subtotal.toLocaleString("en-IN")}
                </p>
                <p className="text-xs opacity-80 leading-tight truncate">
                  {!user
                    ? (theme?.cart_login_hint || "Login required to checkout")
                    : (cartLines.subtotal >= meta.delivery_free_over
                        ? (theme?.cart_free_delivery_label || "Free delivery")
                        : (theme?.cart_delivery_fee_template?.replace("{fee}", String(meta.delivery_fee_flat)) || `+ ₹${meta.delivery_fee_flat} delivery`))}
                </p>
              </div>
            </div>
            <Button onClick={goCheckout} className="rounded-full bg-primary hover:bg-primary/90 flex-shrink-0" data-testid="go-checkout">
              {!user ? (theme?.checkout_login_btn_label || "Login & checkout") : (theme?.checkout_btn_label || "Checkout")} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Dish detail modal — opens when a user taps a dish image */}
      <DishDetailModal
        open={!!openItem}
        item={openItem}
        onClose={() => setOpenItem(null)}
        onAdd={onAdd}
        onBuy={buyNow}
      />

      {/* Landing-page promotional popup — auto-opens once per session when
          an admin has activated the offer. Mounted here AND on /home so the
          promo reaches both the marketing landing and the storefront. */}
      <PromotionPopup />
    </div>
  );
}
